/**
 * useAdminSubscriptions — gestion des souscriptions produit par brand
 *
 * Lecture + mutations sur `BrandProductSubscription` (cf US-MAC.8).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type { Product } from '@bill/core-types';
import {
  adminSubscriptionsService,
  type ActivateSubscriptionInput,
  type BrandSubscriptionsListResponse,
} from '../services/admin-subscriptions.service';

type ApiError = Error & { response?: { data?: { error?: { message?: string } } } };

export const adminSubscriptionsKeys = {
  all: ['admin', 'subscriptions'] as const,
  byBrand: (brandId: string) => [...adminSubscriptionsKeys.all, brandId] as const,
};

/** Liste les souscriptions effectives d'une brand (avec heritage). */
export function useBrandSubscriptions(brandId: string | null) {
  return useQuery<BrandSubscriptionsListResponse>({
    queryKey: brandId ? adminSubscriptionsKeys.byBrand(brandId) : ['admin', 'subscriptions', 'null'],
    queryFn: () => adminSubscriptionsService.list(brandId as string),
    enabled: !!brandId,
  });
}

/** Active un module sur une brand. */
export function useActivateSubscription(brandId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ActivateSubscriptionInput) =>
      adminSubscriptionsService.activate(brandId as string, input),
    onSuccess: (_, input) => {
      if (brandId) {
        queryClient.invalidateQueries({ queryKey: adminSubscriptionsKeys.byBrand(brandId) });
      }
      toast.success(`Module ${input.product} active`);
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.error?.message ?? "Erreur lors de l'activation");
    },
  });
}

/** Desactive un module sur une brand. */
export function useDeactivateSubscription(brandId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (product: Product) =>
      adminSubscriptionsService.deactivate(brandId as string, product),
    onSuccess: (_, product) => {
      if (brandId) {
        queryClient.invalidateQueries({ queryKey: adminSubscriptionsKeys.byBrand(brandId) });
      }
      toast.success(`Module ${product} desactive`);
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la desactivation');
    },
  });
}

/** Toggle le flag isDemo d'une souscription. */
export function useSetSubscriptionDemo(brandId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { product: Product; isDemo: boolean }) =>
      adminSubscriptionsService.setDemo(brandId as string, input.product, input.isDemo),
    onSuccess: (_, input) => {
      if (brandId) {
        queryClient.invalidateQueries({ queryKey: adminSubscriptionsKeys.byBrand(brandId) });
      }
      toast.success(
        input.isDemo
          ? `Mode demo active sur ${input.product}`
          : `Mode demo desactive sur ${input.product}`,
      );
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors du toggle demo');
    },
  });
}

/** Met a jour la limite d'ecrans BDisplay d'une brand. */
export function useSetBdisplayScreenLimit(brandId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (limit: number | null) =>
      adminSubscriptionsService.setBdisplayScreenLimit(brandId as string, limit),
    onSuccess: () => {
      if (brandId) {
        queryClient.invalidateQueries({ queryKey: adminSubscriptionsKeys.byBrand(brandId) });
      }
      toast.success('Limite ecrans BDisplay mise a jour');
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la mise a jour');
    },
  });
}
